import type { TextFieldProps } from '@mui/material/TextField';
import { SxProps, TextField, Theme } from "@mui/material";
import React, { ChangeEvent, ReactElement, ReactNode, forwardRef, useCallback } from "react";
import { SelectOption } from './types';
import { OptionGroup, OptionItem } from './Dropdown.styles';

export type DropdownProps<T extends string | number, E> = {
  label?: ReactNode;
  placeholder?: string;
  value: T | null | undefined;
  options: SelectOption<T, ReactNode, E>[] | null | undefined;
  onChange(e: ChangeEvent<HTMLInputElement>, value: T, entity: E): void;
  onOptionClick?: (value: T) => void;
  renderValue?: (value: T) => ReactNode;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Shows the option whose value is empty while it is selected; without it the field reads as blank. */
  displayEmpty?: boolean;
  size?: TextFieldProps['size'];
  variant?: "outlined" | "filled" | "standard";
  margin?: "dense" | "normal" | "none";
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
};

const DropdownInner = <T extends string | number, E>(
  props: DropdownProps<T, E>,
  ref: React.ForwardedRef<HTMLInputElement>
): ReactElement | null => {
  const {
    label,
    placeholder,
    value,
    options,
    onChange,
    onOptionClick,
    renderValue,
    error,
    helperText,
    required,
    disabled,
    displayEmpty,
    size,
    variant = "outlined",
    margin = "normal",
    fullWidth = true,
    sx,
  } = props;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value as T;
      const entity = options?.find((o) => o.value === value)?.entity as E;
      onChange(e, value, entity);
    },
    [options, onChange]
  );

  return (
    <TextField
      select
      inputRef={ref}
      label={label}
      placeholder={placeholder}
      value={value || ""}
      onChange={handleChange}
      error={error}
      helperText={helperText}
      required={required}
      disabled={disabled}
      InputLabelProps={displayEmpty ? { shrink: true } : undefined}
      size={size}
      variant={variant}
      margin={margin}
      fullWidth={fullWidth}
      sx={sx}
      SelectProps={{
        displayEmpty,
        renderValue: renderValue ? value => renderValue(value as T) : undefined,
      }}
    >
      {options?.flatMap((o, index) => {
        const previousGroup = index > 0 ? options[index - 1]?.group : undefined;
        const showGroupHeader = Boolean(o.group) && o.group !== previousGroup;
        const items: ReactElement[] = [];

        if (showGroupHeader && o.group) {
          items.push(
            <OptionGroup
              key={`group-${o.group}`}
              disableSticky

            >
              {o.group}
            </OptionGroup>
          );
        }

        items.push(
          <OptionItem
            value={o.value}
            key={o.value}
            disabled={o.disabled}
            onClick={onOptionClick ? () => onOptionClick(o.value) : undefined}
            $grouped={Boolean(o.group)}
          >
            {o.title}
          </OptionItem>
        );

        return items;
      })}
    </TextField>
  );
};

export const Dropdown = forwardRef(DropdownInner) as <T extends string | number, E>(
  props: DropdownProps<T, E> & React.RefAttributes<HTMLInputElement>
) => ReactElement | null;

export default Dropdown;
